import * as Sentry from '@sentry/react';
import type { Integration } from '@sentry/core';
import {
	createRoutesFromChildren,
	matchRoutes,
	useLocation,
	useNavigationType,
} from 'react-router-dom';
import { useEffect } from 'react';
import { getOperationAST, Kind, parse } from 'graphql';

type SentryUser = { id: string; username: string };

// The SDK's graphqlClientIntegration records the complete request body, including
// login variables. Use its request hooks to record only operation names instead.
const graphqlOperationNamesIntegration = (): Integration => {
	const operationInfo = (url: unknown, body: unknown) => {
		if (typeof url !== 'string' || !/\/graphql$/.test(url) || typeof body !== 'string') return;
		try {
			const payload = JSON.parse(body);
			if (typeof payload.query !== 'string') return;
			const operation = getOperationAST(parse(payload.query), payload.operationName);
			if (!operation) return;
			const field = operation.selectionSet.selections.find(selection => selection.kind === Kind.FIELD);
			const name = operation.name?.value ?? (field?.kind === Kind.FIELD ? field.name.value : 'anonymous');
			return { name, type: operation.operation, description: `${operation.operation} ${name}` };
		} catch {
			// Telemetry must never prevent a request with an unrecognized body.
			return;
		}
	};
	return {
		name: 'GraphQLOperationNames',
		setup(client) {
			client.on('beforeOutgoingRequestSpan', (span, hint) => {
				const json = Sentry.spanToJSON(span);
				if (json.op !== 'http.client' || !Array.isArray(hint.input)) return;
				const url = json.data?.['url.full'] ?? json.data?.['http.url'];
				const info = operationInfo(url, hint.input[1]?.body);
				if (!info) return;
				const method = json.data?.['http.request.method'] ?? json.data?.['http.method'] ?? 'POST';
				span.updateName(`${method} ${url} (${info.description})`);
				span.setAttribute('graphql.operation.name', info.name);
				span.setAttribute('graphql.operation.type', info.type);
			});
			client.on('beforeOutgoingRequestBreadcrumb', (breadcrumb, hint) => {
				if (breadcrumb.category !== 'fetch' || !Array.isArray(hint.input)) return;
				const info = operationInfo(breadcrumb.data?.url, hint.input[1]?.body);
				if (info && breadcrumb.data) breadcrumb.data['graphql.operation'] = info.description;
			});
		},
	};
};

/**
 * Initialize Sentry error tracking
 * Should be called once at application startup
 */
export const initializeSentry = (user?: SentryUser): void => {
	const dsn = process.env.REACT_APP_SENTRY_DSN;
	const environment = process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV || 'development';

	// Only initialize if DSN is provided and not a placeholder
	if (!dsn || dsn === 'your-sentry-dsn-here') {
		console.warn('Sentry DSN not configured. Error tracking is disabled.');
		return;
	}

	// Set identity before tracing/replay start. Keeping it in initialScope would
	// override later isolation-scope updates, retaining the user after logout.
	if (user) setSentryUser(user);
	else clearSentryUser();

	Sentry.init({
		dsn,
		environment,
		integrations: [
			// React Router integration for better error tracking
			Sentry.reactRouterV7BrowserTracingIntegration({
				useEffect,
				useLocation,
				useNavigationType,
				createRoutesFromChildren,
				matchRoutes,
			}),
			graphqlOperationNamesIntegration(),
			// Replay integration for session recording (optional)
			Sentry.replayIntegration({
				maskAllText: true,
				blockAllMedia: true,
			}),
		],

		// Performance Monitoring
		tracesSampleRate: environment === 'production' ? 0.2 : 1.0,
		tracePropagationTargets: [/^https:\/\/api\.roompulse\.io(?:\/|$)/],
		sendDefaultPii: false,

		// Session Replay
		replaysSessionSampleRate: environment === 'production' ? 0.1 : 1.0, // 10% of sessions
		replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

		// Release tracking
		release: process.env.REACT_APP_VERSION || 'unknown',

		// Application identifier - marks which app sends the logs
		initialScope: {
			tags: {
				application: 'roompulse-rates-app-ui',
				app_type: 'frontend',
				version: process.env.REACT_APP_VERSION || 'unknown',
			},
		},

		// Filter out development errors
		beforeSend(event, hint) {
			// Don't send errors in development unless explicitly enabled
			if (environment === 'development' && !process.env.REACT_APP_SENTRY_ENABLE_DEV) {
				return null;
			}
			return event;
		},

		// Ignore common non-critical errors
		ignoreErrors: [
			// Browser extensions
			'top.GLOBALS',
			'chrome-extension://',
			'moz-extension://',
			// Network errors
			'NetworkError',
			'Network request failed',
			// ResizeObserver errors (harmless)
			'ResizeObserver loop limit exceeded',
			'ResizeObserver loop completed with undelivered notifications',
		],
	});

	console.log(`Sentry initialized for environment: ${environment}`);
};

/**
 * Set user context for Sentry
 */
export const setSentryUser = (user: SentryUser): void => {
	Sentry.setUser({ id: user.id, username: user.username });
};

/**
 * Clear user context from Sentry (e.g., on logout)
 */
export const clearSentryUser = (): void => {
	Sentry.setUser(null);
};

/**
 * Manually capture an exception
 */
export const captureException = (error: Error, context?: Record<string, any>): void => {
	if (context) {
		Sentry.setContext('additional_context', context);
	}
	Sentry.captureException(error);
};

/**
 * Manually capture a message
 */
export const captureMessage = (message: string, level: Sentry.SeverityLevel = 'info'): void => {
	Sentry.captureMessage(message, level);
};

/**
 * Add breadcrumb for debugging
 */
export const addBreadcrumb = (breadcrumb: Sentry.Breadcrumb): void => {
	Sentry.addBreadcrumb(breadcrumb);
};

export default {
	initializeSentry,
	setSentryUser,
	clearSentryUser,
	captureException,
	captureMessage,
	addBreadcrumb,
};
